'use client';

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Globe, Plug, Shield } from 'lucide-react';
import { ProviderIcon } from '@/components/provider-icon';
import { Spinner } from '@/components/ui/spinner';
import { useT } from '@/components/i18n-provider';
import {
  dispatchIntegrationChanged,
  useIntegrationChanged,
} from '@/components/agent-tools/integration-events';
import type { HubMode } from '@/components/agent-tools/parse-hub-params';

interface AgentIntegrationData {
  id: string;
  type: 'platform' | 'custom_mcp';
  provider: string | null;
  name: string | null;
  enabledCapabilities: string[] | null;
  isEnabled: boolean | null;
  metadata?: Record<string, unknown> | null;
  connectedAccountId?: string | null;
  mcpOauthConnected?: boolean;
}

interface McpSkillEntry {
  url?: string;
  authType?: string;
  enabledTools?: string[];
  toolPolicies?: Record<string, 'allow' | 'notify' | 'block'>;
  toolExposureMode?: 'all' | 'whitelist' | 'blacklist';
  catalogId?: string | null;
}

interface OpenApiSkillEntry {
  specUrl: string;
  baseUrl?: string;
  authType?: string;
  operations?: string[];
  operationPolicies?: Record<string, 'allow' | 'notify' | 'block'>;
  operationExposureMode?: 'all' | 'whitelist' | 'blacklist';
}

export interface IntegrationHubFocus {
  brand?: string;
  mode?: HubMode;
  customMcpServerName?: string;
}

interface Props {
  agentConfigId: string;
  agentUserId: string;
  onDirty?: () => void;
  onConfigure?: (focus: IntegrationHubFocus) => void;
}

export interface AgentIntegrationsHandle {
  flush: () => Promise<void>;
}

type ActiveIntegrationKind = 'platform' | 'mcp' | 'openapi';

interface ActiveIntegrationItem {
  key: string;
  kind: ActiveIntegrationKind;
  name: string;
  subtitle: string;
  summary: string;
  iconSlug?: string;
  enabled: boolean;
  integrationId?: string;
  focus: IntegrationHubFocus;
}

const MESSAGING_PROVIDERS = new Set([
  'whatsapp',
  'messenger',
  'instagram',
  'telegram',
  'twilio',
  'slack',
  'discord',
  'linkedin',
  'bluesky',
  'reddit',
  'google-chat',
  'sendgrid',
]);

const MEDIA_GEN_PROVIDERS = new Set(['fal', 'replicate']);

function titleize(value: string): string {
  return value
    .replace(/^mcp:/, '')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function brandFromMcpName(name: string, entry?: McpSkillEntry): string {
  const catalogId = entry?.catalogId;
  if (typeof catalogId === 'string' && catalogId.trim()) {
    return catalogId.replace(/-(admin|storefront)$/, '');
  }
  return name.replace(/^mcp:/, '').replace(/-(admin|storefront)$/, '');
}

function policySummary(
  policies: Record<string, 'allow' | 'notify' | 'block'> | undefined,
  legacyList: string[] | undefined,
): string {
  if (policies && Object.keys(policies).length > 0) {
    let allow = 0;
    let notify = 0;
    let block = 0;
    for (const value of Object.values(policies)) {
      if (value === 'allow') allow += 1;
      else if (value === 'notify') notify += 1;
      else block += 1;
    }
    const bits = [
      allow ? `${allow} allow` : null,
      notify ? `${notify} notify` : null,
      block ? `${block} block` : null,
    ].filter(Boolean);
    return bits.length ? bits.join(' / ') : 'No tools exposed';
  }
  if (legacyList?.length) return `${legacyList.length} tools enabled`;
  return 'Tools configurable in hub';
}

function buildActiveItems(args: {
  integrations: AgentIntegrationData[];
  mcpSkills: Record<string, McpSkillEntry>;
  openapiSkills: Record<string, OpenApiSkillEntry>;
  t: (key: string, params?: Record<string, string | number>) => string;
}): ActiveIntegrationItem[] {
  const { integrations, mcpSkills, openapiSkills, t } = args;
  const items: ActiveIntegrationItem[] = [];

  for (const row of integrations) {
    if (row.type !== 'platform' || !row.provider) continue;
    const provider = row.provider;
    const mode: HubMode = MEDIA_GEN_PROVIDERS.has(provider)
      ? 'api_key'
      : MESSAGING_PROVIDERS.has(provider)
        ? 'messaging'
        : 'oauth';
    const enabledCount = row.enabledCapabilities?.length ?? 0;
    items.push({
      key: `platform:${provider}`,
      kind: 'platform',
      name: titleize(provider),
      subtitle: mode === 'messaging' ? 'Messaging / posting' : mode === 'api_key' ? t('settings.authApiKey') : t('agentIntegrations.oauth'),
      summary: enabledCount > 0 ? `${enabledCount} capabilities enabled` : 'Needs tool selection',
      iconSlug: provider,
      enabled: row.isEnabled !== false,
      integrationId: row.id,
      focus: { brand: provider, mode },
    });
  }

  for (const [name, entry] of Object.entries(mcpSkills)) {
    const matchingRow = integrations.find(
      (row) => row.type === 'custom_mcp' && (row.name === name || row.provider === `mcp:${name}`),
    );
    const brand = brandFromMcpName(name, entry);
    items.push({
      key: `mcp:${name}`,
      kind: 'mcp',
      name: titleize(name),
      subtitle: 'MCP server',
      summary: policySummary(entry.toolPolicies, entry.enabledTools),
      iconSlug: brand,
      enabled: matchingRow?.isEnabled !== false,
      integrationId: matchingRow?.id,
      focus: { brand, mode: 'mcp_hosted', customMcpServerName: name },
    });
  }

  for (const [name, entry] of Object.entries(openapiSkills)) {
    items.push({
      key: `openapi:${name}`,
      kind: 'openapi',
      name: titleize(name),
      subtitle: 'OpenAPI server',
      summary: policySummary(entry.operationPolicies, entry.operations),
      enabled: true,
      focus: { brand: 'custom_openapi', mode: 'openapi', customMcpServerName: name },
    });
  }

  return items.sort((a, b) => a.name.localeCompare(b.name));
}

export const AgentIntegrations = forwardRef<AgentIntegrationsHandle, Props>(
  ({ agentConfigId, agentUserId, onConfigure }, ref) => {
    const t = useT();
    const [loading, setLoading] = useState(true);
    const [savingId, setSavingId] = useState<string | null>(null);
    const [integrations, setIntegrations] = useState<AgentIntegrationData[]>([]);
    const [mcpSkills, setMcpSkills] = useState<Record<string, McpSkillEntry>>({});
    const [openapiSkills, setOpenapiSkills] = useState<Record<string, OpenApiSkillEntry>>({});

    const load = useCallback(async () => {
      setLoading(true);
      try {
        const [intRes, agentRes] = await Promise.all([
          fetch(`/api/agents/${agentConfigId}/integrations`),
          fetch(`/api/agents/${agentUserId}`),
        ]);
        if (intRes.ok) {
          const data = await intRes.json();
          setIntegrations((data.integrations as AgentIntegrationData[]) ?? []);
        }
        if (agentRes.ok) {
          const data = await agentRes.json();
          const skills = data?.config?.skills as Record<string, unknown> | undefined;
          setMcpSkills((skills?.mcp as Record<string, McpSkillEntry>) ?? {});
          setOpenapiSkills((skills?.openapi as Record<string, OpenApiSkillEntry>) ?? {});
        }
      } finally {
        setLoading(false);
      }
    }, [agentConfigId, agentUserId]);

    useEffect(() => {
      void load();
    }, [load]);

    useIntegrationChanged(
      () => {
        void load();
      },
      { agentConfigId },
    );

    useImperativeHandle(ref, () => ({
      // The inline surface no longer buffers integration mutations. All
      // changes are persisted immediately through the dedicated integration
      // endpoints or through the hub.
      flush: async () => {},
    }));

    const items = useMemo(
      () => buildActiveItems({ integrations, mcpSkills, openapiSkills, t }),
      [integrations, mcpSkills, openapiSkills, t],
    );

    const toggleIntegration = useCallback(
      async (item: ActiveIntegrationItem, enabled: boolean) => {
        if (!item.integrationId) {
          onConfigure?.(item.focus);
          return;
        }
        setSavingId(item.key);
        try {
          const res = await fetch(
            `/api/agents/${agentConfigId}/integrations/${item.integrationId}`,
            {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ isEnabled: enabled }),
            },
          );
          if (res.ok) {
            await load();
            dispatchIntegrationChanged({
              reason: 'platform_capability_change',
              ref: item.focus.brand ?? item.name,
              agentConfigId,
            });
          }
        } finally {
          setSavingId(null);
        }
      },
      [agentConfigId, load, onConfigure],
    );

    if (loading) {
      return (
        <div className="flex items-center justify-center rounded-lg border border-border py-6">
          <Spinner size="lg" tone="muted" />
        </div>
      );
    }

    if (items.length === 0) {
      return (
        <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
          No integrations yet{' '}
          <button
            type="button"
            onClick={() => onConfigure?.({})}
            className="font-medium text-foreground underline-offset-4 hover:underline"
          >
            {t('agents.manageIntegrations')}
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {items.map((item) => (
          <div
            key={item.key}
            className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background px-3 py-2.5"
          >
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted">
                {item.kind === 'openapi' ? (
                  <Globe className="h-4 w-4 text-muted-foreground" />
                ) : item.kind === 'mcp' ? (
                  <Shield className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ProviderIcon slug={item.iconSlug ?? item.name} size={18} />
                )}
              </div>
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">{item.name}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {item.subtitle} · {item.summary}
                </div>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={() => onConfigure?.(item.focus)}
                className="btn-ghost text-xs"
              >
                Configure
              </button>
              <button
                type="button"
                onClick={() => toggleIntegration(item, !item.enabled)}
                disabled={savingId === item.key}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  item.enabled ? 'bg-primary' : 'bg-muted-foreground/30 ring-1 ring-border'
                }`}
                aria-label={`${item.enabled ? t('agentIntegrations.disable') : t('agentIntegrations.enable')} ${item.name}`}
              >
                {savingId === item.key ? (
                  <span className="mx-auto">
                    <Spinner size="xs" tone="muted" />
                  </span>
                ) : (
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform ${
                      item.enabled ? 'translate-x-4.5' : 'translate-x-0.5'
                    }`}
                  />
                )}
              </button>
            </div>
          </div>
        ))}
        <div className="flex items-center gap-2 pt-1 text-xs text-muted-foreground">
          <Plug className="h-3.5 w-3.5" />
          <span>{t('agents.manageIntegrationsHint')}</span>
        </div>
      </div>
    );
  },
);

AgentIntegrations.displayName = 'AgentIntegrations';
